import { useEffect, useState } from 'react';
import { usePsychologistContext } from '../contexts/PsychologistContext';
import { useAuth } from '../contexts/AuthContext';
import apiCoreAdapter from './apiCoreAdapter';
import { ContactChannel, NZ_CONTACT, STANDARD_CONTACT } from '../utils/contactChannel';

export interface UseContactChannelResult extends ContactChannel {
  loading: boolean;
  /**
   * `true` solo para el segmento nuevo (`psychologist_psichat_inbox`), nunca para quien ya
   * tenía canal de administración por `fee_type=range` (NZ/O-desde-NZ) — el backend ya hace
   * esa exclusión (ver `show_contact_channel_notice` en psychologist-profile.service.ts).
   * Pensado para el popup de aviso único, no para resolver el canal en sí.
   */
  showContactChannelNotice: boolean;
}

interface ResolvedChannel {
  usesAdminContactChannel: boolean;
  showContactChannelNotice: boolean;
}

// Cache en memoria de módulo, keyeada por psychologistId: mismo patrón que useRankingTransition
// — un consumidor nuevo de este hook no dispara un fetch nuevo, reusa el resultado ya resuelto.
const channelCache = new Map<string, ResolvedChannel>();
const channelPending = new Map<string, Promise<ResolvedChannel>>();

const FAIL_CLOSED: ResolvedChannel = { usesAdminContactChannel: false, showContactChannelNotice: false };

function useResolvedChannel(psychologistId: string): ResolvedChannel & { loading: boolean } {
  const cached = channelCache.get(psychologistId);
  const [resolved, setResolved] = useState(cached ?? FAIL_CLOSED);
  const [loading, setLoading] = useState(!!psychologistId && cached === undefined);

  useEffect(() => {
    let cancelled = false;
    if (!psychologistId) { setLoading(false); return () => { cancelled = true; }; }

    const alreadyCached = channelCache.get(psychologistId);
    if (alreadyCached !== undefined) {
      setResolved(alreadyCached);
      setLoading(false);
      return () => { cancelled = true; };
    }

    setLoading(true);
    let fetchPromise = channelPending.get(psychologistId);
    if (!fetchPromise) {
      fetchPromise = apiCoreAdapter.psychologistProfile().then((profile) => ({
        usesAdminContactChannel: !!profile.uses_admin_contact_channel,
        showContactChannelNotice: !!profile.show_contact_channel_notice,
      }));
      channelPending.set(psychologistId, fetchPromise);
    }
    fetchPromise
      .then((value) => {
        channelCache.set(psychologistId, value);
        if (!cancelled) setResolved(value);
      })
      .catch(() => { /* fail-closed: queda canal estándar, sin popup */ })
      .finally(() => {
        channelPending.delete(psychologistId);
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [psychologistId]);

  return { ...resolved, loading };
}

/**
 * Método de contacto (mail + WhatsApp) que le corresponde a la psicóloga actual. La resolución
 * (fee_type=range o segmento nuevo en psychologist_psichat_inbox) la hace el backend — ver
 * `uses_admin_contact_channel`/`show_contact_channel_notice` en GET /api/psychologist/profile,
 * porque el frontend no tiene acceso directo a ninguna de esas dos fuentes (`fee_type` es una
 * columna de MySQL que no viaja hoy por ningún endpoint que consuma el FE, y la tabla del
 * segmento nuevo vive en una Supabase de otro proyecto). Cachea el fetch por psychologistId —
 * llamar este hook desde múltiples componentes no multiplica el fetch.
 */
export function useContactChannel(): UseContactChannelResult {
  const { psychologist } = usePsychologistContext();
  const { psychologistId: authPsychologistId } = useAuth();
  const resolvedId = authPsychologistId || psychologist?.id || '';

  const { usesAdminContactChannel, showContactChannelNotice, loading } = useResolvedChannel(resolvedId);
  const channel = usesAdminContactChannel ? NZ_CONTACT : STANDARD_CONTACT;
  return { ...channel, loading, showContactChannelNotice };
}
